import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useToast } from '../hooks/use-toast';
import { Music, Link as LinkIcon, AlertCircle, CheckCircle, Upload as UploadIcon, Loader2, Info } from 'lucide-react';
import { getAvatarUrl, getInitials } from '../utils/avatarUtils';
import PaymentStep from '../components/PaymentStep';
import { SubmitPageNudge } from '../components/SupportNudge';
import { trackEvent } from '../utils/analytics';
import NewReviewersCarousel from '../components/NewReviewersCarousel';

const API_URL = '';  // Use relative URLs with proxy

// Generate unique submission ID for idempotency
const generateSubmissionKey = (formData, file, streamingLink) => {
  const data = `${formData.reviewerId}-${formData.title}-${formData.artist}-${file?.name || ''}-${streamingLink || ''}-${Date.now()}`;
  // Simple hash for idempotency
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `sub_${Math.abs(hash).toString(36)}_${Date.now()}`;
};

const Upload = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { reviewerUsername } = useParams(); // Support /upload/:reviewerUsername route
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    email: '',
    genre: 'Hip-Hop',
    reviewerId: searchParams.get('reviewer') || '',
    submissionType: 'skip',  // Default to Skip ($5) for better conversion
    notes: '',
    notifyOnLive: false  // NEW: Consent to receive "go live" notifications
  });
  
  const [file, setFile] = useState(null);
  const [streamingLink, setStreamingLink] = useState('');
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'link'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewers, setReviewers] = useState([]);
  const [loadingReviewers, setLoadingReviewers] = useState(true);
  const [selectedReviewerInfo, setSelectedReviewerInfo] = useState(null);
  const [reviewerSession, setReviewerSession] = useState(null); // Active session info
  
  // P2: Payment step state
  const [showPaymentStep, setShowPaymentStep] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [customTipAmount, setCustomTipAmount] = useState(1500); // $15 default (in cents)
  const [customTipInput, setCustomTipInput] = useState('15'); // Separate input state for better UX
  
  // Ref to track previous skips_only_mode for change detection (survives re-renders)
  const prevSkipsOnlyModeRef = useRef(undefined);
  
  // Ref to track previous submissions_enabled for change detection
  const prevSubmissionsEnabledRef = useRef(undefined);
  
  // Flag to track if we&apos;re processing a Stripe redirect return
  const [isProcessingStripeReturn, setIsProcessingStripeReturn] = useState(false);
  const stripeReturnProcessedRef = useRef(false);
  
  // State for pre-uploaded file (uploaded before payment redirect)
  const [preUploadedFileUrl, setPreUploadedFileUrl] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isPreUploadingFile, setIsPreUploadingFile] = useState(false);

  // Helper function to upload file BEFORE Stripe redirect (so it survives the redirect)
  const preUploadFileForRedirect = async () => {
    if (uploadType !== 'file') {
      console.log('[PRE-UPLOAD] Not a file upload, skipping');
      return null; // No file to upload
    }
    
    if (!file) {
      console.error('[PRE-UPLOAD] No file selected but uploadType is file');
      toast({
        title: "No File Selected",
        description: "Please select a file to upload.",
        variant: "destructive"
      });
      return null;
    }
    
    console.log('[PRE-UPLOAD] Starting pre-upload for file:', file.name, 'size:', file.size);
    setIsPreUploadingFile(true);
    
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      
      console.log('[PRE-UPLOAD] Sending file to /api/submissions/pre-upload...');
      const response = await axios.post(`${API_URL}/api/submissions/pre-upload`, uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60 second timeout for large files
      });
      
      console.log('[PRE-UPLOAD] Success! File URL:', response.data.file_url);
      setPreUploadedFileUrl(response.data.file_url);
      return response.data.file_url;
    } catch (error) {
      console.error('[PRE-UPLOAD] Failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to upload your file.";
      toast({
        title: "File Upload Error",
        description: errorMessage,
        variant: "destructive"
      });
      return null;
    } finally {
      setIsPreUploadingFile(false);
    }
  };

  // Helper function to save form state before Stripe redirect
  const saveFormStateForStripeRedirect = async (paymentInfo) => {
    let fileUrlToSave = preUploadedFileUrl;
    
    // If file upload and no pre-uploaded URL yet, upload now
    if (uploadType === 'file' && file && !fileUrlToSave) {
      fileUrlToSave = await preUploadFileForRedirect();
      if (!fileUrlToSave) {
        console.error('[STRIPE REDIRECT] Cannot proceed - file upload failed');
        return false; // Signal that save failed
      }
    }
    
    const stateToSave = {
      formData,
      streamingLink,
      uploadType,
      preUploadedFileUrl: fileUrlToSave, // Save the pre-uploaded file URL
      customTipAmount,
      customTipInput,
      paymentInfo, // Contains payment_id from backend
      timestamp: Date.now()
    };
    
    sessionStorage.setItem('stripePaymentPendingSubmission', JSON.stringify(stateToSave));
    console.log('[STRIPE REDIRECT] Saved form state to sessionStorage:', stateToSave);
    return true;
  };

  // Handle Stripe redirect return - check URL for payment_intent parameter
  useEffect(() => {
    // Only process once
    if (stripeReturnProcessedRef.current) return;

    const paymentIntent = searchParams.get('payment_intent');
    // eslint-disable-next-line no-unused-vars
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
    const redirectStatus = searchParams.get('redirect_status');

    if (paymentIntent && redirectStatus) {
      stripeReturnProcessedRef.current = true;
      setIsProcessingStripeReturn(true);
      console.log('[STRIPE REDIRECT] Detected return from Stripe:', { paymentIntent, redirectStatus });

      // Retrieve saved form state
      const savedStateJson = sessionStorage.getItem('stripePaymentPendingSubmission');
      
      if (!savedStateJson) {
        console.error('[STRIPE REDIRECT] No saved form state found in sessionStorage');
        toast({
          title: "Payment Error",
          description: "Your form data was lost. Please re-submit your track.",
          variant: "destructive"
        });
        setIsProcessingStripeReturn(false);
        // Clean URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      try {
        const savedState = JSON.parse(savedStateJson);
        console.log('[STRIPE REDIRECT] Retrieved saved state:', savedState);

        // Check if saved state is not too old (5 minutes max)
        if (Date.now() - savedState.timestamp > 5 * 60 * 1000) {
          console.error('[STRIPE REDIRECT] Saved state expired');
          toast({
            title: "Session Expired",
            description: "Your payment session has expired. Please try again.",
            variant: "destructive"
          });
          sessionStorage.removeItem('stripePaymentPendingSubmission');
          setIsProcessingStripeReturn(false);
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }

        // Restore form state
        setFormData(savedState.formData);
        setStreamingLink(savedState.streamingLink);
        setUploadType(savedState.uploadType);
        setCustomTipAmount(savedState.customTipAmount);
        setCustomTipInput(savedState.customTipInput);

        // Handle based on redirect status
        if (redirectStatus === 'succeeded') {
          toast({
            title: "Payment Successful!",
            description: "Completing your submission...",
          });

          // Confirm payment on backend and submit
          const confirmAndSubmit = async () => {
            try {
              // Confirm the payment with backend
              const confirmResponse = await axios.post(
                `${API_URL}/api/payments/confirm-stripe?payment_intent_id=${paymentIntent}`
              );
              
              console.log('[STRIPE REDIRECT] Payment confirmed:', confirmResponse.data);

              // Clear saved state and URL params first
              sessionStorage.removeItem('stripePaymentPendingSubmission');
              window.history.replaceState({}, document.title, window.location.pathname);

              // Now submit the track with the payment_id
              // Use the restored state directly since setState is async
              await submitAfterStripeRedirect(
                savedState.formData,
                savedState.streamingLink,
                savedState.uploadType,
                confirmResponse.data.payment_id,
                savedState.preUploadedFileUrl // Pass pre-uploaded file URL
              );
            } catch (err) {
              console.error('[STRIPE REDIRECT] Error confirming payment:', err);
              toast({
                title: "Submission Error",
                description: err.response?.data?.detail || "Failed to complete submission. Please contact support.",
                variant: "destructive"
              });
              sessionStorage.removeItem('stripePaymentPendingSubmission');
              window.history.replaceState({}, document.title, window.location.pathname);
            } finally {
              setIsProcessingStripeReturn(false);
            }
          };

          confirmAndSubmit();
        } else {
          // Payment failed or was cancelled
          console.log('[STRIPE REDIRECT] Payment not successful:', redirectStatus);
          toast({
            title: "Payment Not Completed",
            description: `Payment status: ${redirectStatus}. Please try again.`,
            variant: "destructive"
          });
          sessionStorage.removeItem('stripePaymentPendingSubmission');
          setIsProcessingStripeReturn(false);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (err) {
        console.error('[STRIPE REDIRECT] Error parsing saved state:', err);
        sessionStorage.removeItem('stripePaymentPendingSubmission');
        setIsProcessingStripeReturn(false);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, toast]);

  // Special submit function for after Stripe redirect (uses passed-in state, not component state)
  const submitAfterStripeRedirect = async (restoredFormData, restoredStreamingLink, restoredUploadType, payment_id, restoredPreUploadedFileUrl) => {
    console.log('[STRIPE REDIRECT SUBMIT] Starting submission with:', {
      formData: restoredFormData,
      streamingLink: restoredStreamingLink,
      uploadType: restoredUploadType,
      payment_id,
      preUploadedFileUrl: restoredPreUploadedFileUrl
    });

    const token = localStorage.getItem('access_token');

    // Validation
    if (!restoredFormData.title || !restoredFormData.artist || !restoredFormData.genre || !restoredFormData.reviewerId) {
      console.error('[STRIPE REDIRECT SUBMIT] Validation failed - missing fields');
      toast({
        title: "Missing information",
        description: "Some required fields were lost. Please re-submit your track.",
        variant: "destructive"
      });
      return;
    }

    // Check we have either a streaming link OR a pre-uploaded file URL
    if (restoredUploadType === 'link' && !restoredStreamingLink) {
      console.error('[STRIPE REDIRECT SUBMIT] Missing streaming link');
      toast({
        title: "Submission Error",
        description: "Streaming link was lost. Please re-submit your track.",
        variant: "destructive"
      });
      return;
    }
    
    if (restoredUploadType === 'file' && !restoredPreUploadedFileUrl) {
      console.error('[STRIPE REDIRECT SUBMIT] Missing pre-uploaded file URL');
      toast({
        title: "Submission Error",
        description: "Your uploaded file was lost. Please re-submit your track.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    const idempotencyKey = generateSubmissionKey(restoredFormData, null, restoredStreamingLink || restoredPreUploadedFileUrl);

    try {
      const submissionData = new FormData();
      submissionData.append('title', restoredFormData.title);
      submissionData.append('artist', restoredFormData.artist);
      submissionData.append('genre', restoredFormData.genre);
      submissionData.append('reviewer_id', restoredFormData.reviewerId);
      submissionData.append('submission_type', restoredFormData.submissionType);
      submissionData.append('idempotency_key', idempotencyKey);
      submissionData.append('payment_id', payment_id);
      
      // Add notification consent
      if (restoredFormData.notifyOnLive) {
        submissionData.append('notify_on_live', 'true');
      }
      
      // Add either streaming link or pre-uploaded file URL
      if (restoredUploadType === 'link') {
        submissionData.append('streaming_link', restoredStreamingLink);
      } else if (restoredUploadType === 'file' && restoredPreUploadedFileUrl) {
        submissionData.append('pre_uploaded_file_url', restoredPreUploadedFileUrl);
      }

      if (restoredFormData.notes) {
        submissionData.append('notes', restoredFormData.notes);
      }

      console.log('[STRIPE REDIRECT SUBMIT] Making API call...');
      const response = await axios.post(
        `${API_URL}/api/submissions`,
        submissionData,
        {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('[STRIPE REDIRECT SUBMIT] Success:', response.data);

      const redirectInfo = response.data?.redirect;
      const submissionId = response.data?.submission?.id;

      // Navigate to appropriate page
      if (redirectInfo?.session_id) {
        navigate(`/session/${redirectInfo.session_id}?new_submission=${submissionId}`);
      } else if (redirectInfo?.reviewer_username) {
        navigate(`/reviewer/${redirectInfo.reviewer_username}?queued=true&submission=${submissionId}`);
      } else {
        navigate(`/submission-success?reviewer=${restoredFormData.reviewerId}`);
      }

      toast({
        title: "✅ Submission successful!",
        description: "Your track has been added to the queue.",
        duration: 4000
      });
    } catch (error) {
      console.error('[STRIPE REDIRECT SUBMIT] Error:', error);
      toast({
        title: "Submission failed",
        description: error.response?.data?.detail || "Please contact support - your payment was received.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load all reviewers on mount
  useEffect(() => {
    const loadReviewers = async () => {
      try {
        setLoadingReviewers(true);
        const response = await axios.get(`${API_URL}/api/reviewers/all`);
        setReviewers(response.data || []);
        
        // If reviewerUsername is in URL path (e.g., /upload/jxhnjacxb), find by username
        if (reviewerUsername && !formData.reviewerId) {
          const matchedReviewer = response.data.find(
            r => r.username?.toLowerCase() === reviewerUsername.toLowerCase()
          );
          if (matchedReviewer) {
            setFormData(prev => ({ ...prev, reviewerId: matchedReviewer.id }));
            setSelectedReviewerInfo(matchedReviewer);
          }
        }
        // If reviewerId is already set from URL query param, load that reviewer&apos;s info
        else if (formData.reviewerId) {
          const preselected = response.data.find(r => r.id === formData.reviewerId);
          if (preselected) {
            setSelectedReviewerInfo(preselected);
          }
        }
      } catch (error) {
        console.error('Error loading reviewers:', error);
        toast({
          title: "Error loading reviewers",
          description: "Unable to load reviewer list. Please refresh the page.",
          variant: "destructive"
        });
      } finally {
        setLoadingReviewers(false);
      }
    };
    loadReviewers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewerUsername]);

  // Update selected reviewer info when reviewerId changes
  useEffect(() => {
    if (formData.reviewerId && reviewers.length > 0) {
      const reviewer = reviewers.find(r => r.id === formData.reviewerId);
      setSelectedReviewerInfo(reviewer || null);
      
      // If payments are disabled (no Stripe or skip line disabled), auto-switch to regular submission
      if (reviewer && (reviewer.skip_line_enabled === false || reviewer.can_accept_payments === false)) {
        if (['skip', 'superskip', 'custom'].includes(formData.submissionType)) {
          setFormData(prev => ({ ...prev, submissionType: 'regular' }));
        }
      }
    } else {
      setSelectedReviewerInfo(null);
      setReviewerSession(null);
    }
  }, [formData.reviewerId, reviewers, formData.submissionType]);

  // Real-time polling for reviewer's session (including skips_only_mode and submissions_enabled)
  // This ensures the Upload page reflects changes in real-time when reviewer toggles settings
  useEffect(() => {
    if (!formData.reviewerId) {
      setReviewerSession(null);
      prevSkipsOnlyModeRef.current = undefined;
      prevSubmissionsEnabledRef.current = undefined;
      return;
    }

    // Function to fetch reviewer&apos;s active session
    const fetchReviewerSession = async () => {
      try {
        const sessionRes = await axios.get(`${API_URL}/api/reviewers/${formData.reviewerId}/active-session`);
        const newSessionData = sessionRes.data;
        const newSkipsOnlyMode = newSessionData?.skips_only_mode || false;
        const newSubmissionsEnabled = newSessionData?.submissions_enabled !== false; // Default to true
        const prevSkipsOnlyMode = prevSkipsOnlyModeRef.current;
        const prevSubmissionsEnabled = prevSubmissionsEnabledRef.current;
        
        // Debug logging
        console.log('[Session Poll] SkipsOnly:', newSkipsOnlyMode, 'SubmissionsEnabled:', newSubmissionsEnabled);
        
        // Check for skips_only_mode state change
        if (prevSkipsOnlyMode !== undefined && newSkipsOnlyMode !== prevSkipsOnlyMode) {
          if (newSkipsOnlyMode && !prevSkipsOnlyMode) {
            // Changed from OFF to ON
            if (formData.submissionType === 'regular') {
              setFormData(prev => ({ ...prev, submissionType: 'skip' }));
            }
            toast({
              title: "⚠️ Skips Only Mode Activated",
              description: "This reviewer is now only accepting paid submissions.",
              variant: "default"
            });
          } else if (!newSkipsOnlyMode && prevSkipsOnlyMode) {
            // Changed from ON to OFF
            toast({
              title: "✅ Free Submissions Available",
              description: "This reviewer is now accepting free submissions!",
              variant: "default"
            });
          }
        }
        
        // Check for submissions_enabled state change
        if (prevSubmissionsEnabled !== undefined && newSubmissionsEnabled !== prevSubmissionsEnabled) {
          if (!newSubmissionsEnabled && prevSubmissionsEnabled) {
            // Submissions were just turned OFF
            toast({
              title: "🚫 Submissions Closed",
              description: "This reviewer has stopped accepting submissions. Please try again later.",
              variant: "destructive"
            });
          } else if (newSubmissionsEnabled && !prevSubmissionsEnabled) {
            // Submissions were just turned ON
            toast({
              title: "✅ Submissions Open",
              description: "This reviewer is now accepting submissions!",
              variant: "default"
            });
          }
        }
        
        // Update refs for next comparison
        prevSkipsOnlyModeRef.current = newSkipsOnlyMode;
        prevSubmissionsEnabledRef.current = newSubmissionsEnabled;
        
        // Always update the session state
        setReviewerSession(newSessionData);
      } catch (error) {
        // No active session is fine
        console.log('[Session Poll] Error or no session:', error.message);
        setReviewerSession(null);
        prevSkipsOnlyModeRef.current = undefined;
        prevSubmissionsEnabledRef.current = undefined;
      }
    };

    // Initial fetch
    fetchReviewerSession();

    // Poll every 3 seconds for real-time updates
    const pollInterval = setInterval(fetchReviewerSession, 3000);

    // Cleanup on unmount or when reviewerId changes
    return () => clearInterval(pollInterval);
  }, [formData.reviewerId, formData.submissionType, toast]);

  // P1: File size limit (50MB matches backend)
  const MAX_FILE_SIZE_MB = 50;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/wave', 'audio/x-wav', 'audio/m4a', 'audio/mp4'];
  const ALLOWED_EXTENSIONS = ['.mp3', '.wav', '.m4a'];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // P1: Client-side file size validation (Item 1.4)
      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        toast({
          title: "File too large",
          description: `Maximum file size is ${MAX_FILE_SIZE_MB}MB. Your file is ${(selectedFile.size / (1024 * 1024)).toFixed(1)}MB.`,
          variant: "destructive"
        });
        e.target.value = ''; // Clear the input
        return;
      }
      
      // P1: Client-side MIME type validation (Item 1.2)
      const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase();
      const isValidType = ALLOWED_AUDIO_TYPES.includes(selectedFile.type) || ALLOWED_EXTENSIONS.includes(fileExtension);
      
      if (!isValidType) {
        toast({
          title: "Invalid file format",
          description: `Please upload an MP3, WAV, or M4A file. Received: ${selectedFile.type || fileExtension}`,
          variant: "destructive"
        });
        e.target.value = ''; // Clear the input
        return;
      }
      
      console.log(`[P1] File validated: ${selectedFile.name}, ${(selectedFile.size / (1024 * 1024)).toFixed(2)}MB, ${selectedFile.type}`);
      setFile(selectedFile);
    }
  };
  
  const handleFilePickerClick = () => {
    // Programmatically trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // P2: Check if payment is required
  const isPaidSubmission = () => {
    return ['skip', 'superskip', 'custom'].includes(formData.submissionType);
  };

  // P2: Handle initiating payment
  const handleInitiatePayment = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.artist || !formData.email || !formData.genre || !formData.reviewerId) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    if (uploadType === 'file' && !file) {
      toast({
        title: "No file selected",
        description: "Please select a music file to upload.",
        variant: "destructive"
      });
      return;
    }
    
    if (uploadType === 'link' && !streamingLink) {
      toast({
        title: "No streaming link",
        description: "Please enter a streaming link.",
        variant: "destructive"
      });
      return;
    }
    
    // Show payment step
    setShowPaymentStep(true);
  };

  // P2: Handle payment completion
  const handlePaymentComplete = (paymentInfo) => {
    console.log('[PAYMENT] handlePaymentComplete called with:', paymentInfo);
    console.log('[PAYMENT] Current formData:', formData);
    console.log('[PAYMENT] Current uploadType:', uploadType);
    console.log('[PAYMENT] Current streamingLink:', streamingLink);
    console.log('[PAYMENT] Current file:', file);
    
    setPaymentId(paymentInfo.payment_id);
    setShowPaymentStep(false);
    
    // Now submit the track with the payment_id
    console.log('[PAYMENT] About to call submitWithPayment...');
    submitWithPayment(paymentInfo.payment_id);
  };

  // P2: Handle payment cancellation
  const handlePaymentCancel = () => {
    setShowPaymentStep(false);
  };

  // P2: Downgrade to free submission
  const handleDowngradeToFree = () => {
    // Block downgrade when skips_only_mode is active
    if (reviewerSession?.skips_only_mode) {
      toast({
        title: "Cannot switch to free",
        description: "This reviewer requires paid submissions. Please complete payment.",
        variant: "destructive"
      });
      return;
    }
    setShowPaymentStep(false);
    setFormData(prev => ({ ...prev, submissionType: 'regular' }));
    toast({
      title: "Submission type changed",
      description: "Your submission will be added to the regular queue.",
    });
  };

  // P2: Submit with payment
  const submitWithPayment = async (payment_id) => {
    await doSubmit(payment_id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Block free submissions when skips_only_mode is active
    if (reviewerSession?.skips_only_mode && formData.submissionType === 'regular') {
      toast({
        title: "Paid submissions only",
        description: "This reviewer is only accepting paid insight submissions right now.",
        variant: "destructive"
      });
      return;
    }
    
    // P2: If paid submission, show payment step first
    if (isPaidSubmission() && !paymentId) {
      handleInitiatePayment(e);
      return;
    }
    
    // Regular (free) submission or already paid
    await doSubmit(paymentId);
  };

  const doSubmit = async (payment_id = null) => {
    console.log('[SUBMIT] doSubmit called with payment_id:', payment_id);
    console.log('[SUBMIT] formData at submit time:', formData);
    console.log('[SUBMIT] uploadType:', uploadType);
    console.log('[SUBMIT] streamingLink:', streamingLink);
    console.log('[SUBMIT] file:', file);
    
    // GA4: Track artist submit start
    trackEvent('artist_submit_start');
    
    const token = localStorage.getItem('access_token');
    // Token is optional - anonymous submissions allowed
    
    // Validation
    if (!formData.title || !formData.artist || !formData.email || !formData.genre || !formData.reviewerId) {
      console.log('[SUBMIT] Validation failed - missing fields:', {
        title: formData.title,
        artist: formData.artist,
        genre: formData.genre,
        reviewerId: formData.reviewerId
      });
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    if (uploadType === 'file' && !file) {
      toast({
        title: "No file selected",
        description: "Please select a music file to upload.",
        variant: "destructive"
      });
      return;
    }

    if (uploadType === 'link' && !streamingLink) {
      console.log('[SUBMIT] Validation failed - no streaming link. uploadType:', uploadType, 'streamingLink:', streamingLink);
      toast({
        title: "No link provided",
        description: "Please provide a streaming link.",
        variant: "destructive"
      });
      return;
    }

    // P1: Double submit protection - disable immediately and generate idempotency key
    if (isSubmitting) {
      console.log('[P1] Blocked duplicate submission attempt');
      return;
    }
    
    setIsSubmitting(true);
    const idempotencyKey = generateSubmissionKey(formData, file, streamingLink);
    console.log('[P1] Submission started with idempotency key:', idempotencyKey);

    try {
      // Prepare form data for backend
      const submissionData = new FormData();
      submissionData.append('title', formData.title);
      submissionData.append('artist', formData.artist);
      submissionData.append('email', formData.email);
      submissionData.append('genre', formData.genre);
      submissionData.append('reviewer_id', formData.reviewerId);
      submissionData.append('submission_type', formData.submissionType);
      submissionData.append('idempotency_key', idempotencyKey); // P1: Send idempotency key
      
      // Add notification consent
      if (formData.notifyOnLive) {
        submissionData.append('notify_on_live', 'true');
      }
      
      // P2: Include payment_id for paid submissions
      if (payment_id) {
        submissionData.append('payment_id', payment_id);
        console.log('[P2] Including payment_id:', payment_id);
      }
      
      if (formData.notes) {
        submissionData.append('notes', formData.notes);
      }

      if (uploadType === 'file' && file) {
        submissionData.append('file', file);
        console.log('[SUBMIT] Adding file to submission');
      } else if (uploadType === 'link' && streamingLink) {
        submissionData.append('streaming_link', streamingLink);
        console.log('[SUBMIT] Adding streaming link:', streamingLink);
      } else {
        console.log('[SUBMIT] WARNING: No file or streaming link!');
      }

      console.log('[SUBMIT] Making API call to /api/submissions...');
      const response = await axios.post(
        `${API_URL}/api/submissions`,
        submissionData,
        {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      console.log('[SUBMIT] API response:', response.data);

      // GA4: Track artist submit complete with payment type
      const paymentType = payment_id ? formData.submissionType : 'free';
      trackEvent('artist_submit_complete', { payment_type: paymentType });

      // P1: Build destination URL for "View your submission" link
      const redirectInfo = response.data?.redirect;
      const submissionId = response.data?.submission?.id;
      
      let destinationUrl = '';
      if (redirectInfo?.session_id) {
        // Pass the new submission ID to trigger Zone A immediately
        destinationUrl = `/session/${redirectInfo.session_id}?new_submission=${submissionId}`;
      } else if (redirectInfo?.reviewer_username) {
        destinationUrl = `/reviewer/${redirectInfo.reviewer_username}?queued=true&submission=${submissionId}`;
      }

      // P1: Store redirect info for recovery (Item 5: 4.2)
      if (redirectInfo) {
        sessionStorage.setItem('pendingRedirect', JSON.stringify({
          redirectInfo,
          submissionId,
          destinationUrl,
          timestamp: Date.now()
        }));
      }
      
      // Store submission data for Hot Seat prompt (Millionaire Cafe integration)
      const submissionDataForHotSeat = {
        id: submissionId,
        title: formData.title,
        artist: formData.artist,
        email: formData.email,
        genre: formData.genre,
        file_url: response.data?.submission?.file_url || null,
        streaming_link: streamingLink || null,
        timestamp: Date.now()
      };
      sessionStorage.setItem('hotSeatSubmission', JSON.stringify(submissionDataForHotSeat));
      
      // IMMEDIATE REDIRECT - Don&apos;t wait for toast, redirect first for faster UX
      if (redirectInfo?.session_id) {
        // Reviewer has active session - go to session dashboard immediately
        navigate(`/session/${redirectInfo.session_id}?new_submission=${submissionId}`);
      } else if (redirectInfo?.reviewer_username) {
        // P1: No active session - navigate immediately, show toast on arrival
        navigate(`/reviewer/${redirectInfo.reviewer_username}?queued=true&submission=${submissionId}`);
      } else {
        // Fallback to submission success page
        navigate(`/submission-success?reviewer=${formData.reviewerId}`);
      }
      
      // Show toast AFTER navigation is triggered (non-blocking)
      toast({
        title: "✅ Submission successful!",
        description: "Your track has been added to the queue.",
        duration: 4000
      });

      // Reset form
      setFormData({
        title: '',
        artist: '',
        email: '',
        genre: 'Hip-Hop',
        reviewerId: formData.reviewerId, // Keep reviewer selected
        submissionType: 'regular',
        notes: ''
      });
      setFile(null);
      setStreamingLink('');
      
      // P1: Clear pending redirect on successful navigation
      sessionStorage.removeItem('pendingRedirect');

    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Submission failed",
        description: error.response?.data?.detail || "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // P1: Check for pending redirect on mount (Item 5: 4.2)
  useEffect(() => {
    const pendingRedirect = sessionStorage.getItem('pendingRedirect');
    if (pendingRedirect) {
      try {
        const { redirectInfo, submissionId, timestamp } = JSON.parse(pendingRedirect);
        // Only recover redirects from last 5 minutes
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          console.log('[P1] Recovering pending redirect:', redirectInfo);
          toast({
            title: "Resuming navigation",
            description: "Your submission was successful. Redirecting...",
          });
          sessionStorage.removeItem('pendingRedirect');
          if (redirectInfo?.session_id) {
            navigate(`/session/${redirectInfo.session_id}`);
          } else if (redirectInfo?.reviewer_username) {
            navigate(`/reviewer/${redirectInfo.reviewer_username}?queued=true&submission=${submissionId}`);
          }
        } else {
          // Expired redirect, clean up
          sessionStorage.removeItem('pendingRedirect');
        }
      } catch (e) {
        sessionStorage.removeItem('pendingRedirect');
      }
    }
  }, [navigate, toast]);
  
  // Check if form is valid and ready to submit
  const isFormValid = () => {
    if (!formData.title || !formData.artist || !formData.email || !formData.genre || !formData.reviewerId) {
      return false;
    }
    if (uploadType === 'file' && !file) {
      return false;
    }
    if (uploadType === 'link' && !streamingLink) {
      return false;
    }
    return true;
  };

  // Show loading overlay when processing Stripe redirect return
  if (isProcessingStripeReturn) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-black mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-black mb-2">Completing Your Payment...</h2>
          <p className="text-gray-600">Please wait while we process your submission.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-black mb-2">Submit Your Track</h1>
          <p className="text-gray-600">Get real time insight and encouragement from live reviewers</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Submissions Disabled Warning */}
          {reviewerSession && reviewerSession.submissions_enabled === false && (
            <Card className="p-6 mb-6 bg-red-50 border-red-300">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-full">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-800">Submissions Closed</h3>
                  <p className="text-red-700">
                    This reviewer is not accepting submissions at this time. Please check back later or choose a different reviewer.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Upload Type Selection */}
          <Card className="p-6 mb-6">
            <Label className="text-base font-semibold mb-3 block">Choose Submission Method</Label>
            <div className="grid grid-cols-2 gap-4">
              <Button
                type="button"
                variant={uploadType === 'file' ? 'default' : 'outline'}
                onClick={() => setUploadType('file')}
                className={uploadType === 'file' ? 'bg-black' : ''}
              >
                <Music className="h-5 w-5 mr-2" />
                Upload MP3/WAV
              </Button>
              <Button
                type="button"
                variant={uploadType === 'link' ? 'default' : 'outline'}
                onClick={() => setUploadType('link')}
                className={uploadType === 'link' ? 'bg-black' : ''}
              >
                <LinkIcon className="h-5 w-5 mr-2" />
                Streaming Link
              </Button>
            </div>
          </Card>

          {/* File Upload or Link */}
          <Card className="p-6 mb-6">
            <div className="space-y-6">
              {/* File Upload or Link */}
              {uploadType === 'file' ? (
                <div>
                  <Label className="text-base font-semibold mb-2 block">
                    Music File (MP3/WAV, max 100MB) *
                  </Label>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#F2D857] transition-colors cursor-pointer"
                    onClick={handleFilePickerClick}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="file"
                      accept=".mp3,.wav,audio/mpeg,audio/wav"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <UploadIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    {file ? (
                      <div>
                        <p className="text-green-600 font-semibold mb-1">
                          <CheckCircle className="inline h-5 w-5 mr-1" />
                          {file.name}
                        </p>
                        <p className="text-sm text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                        <p className="text-xs text-gray-400 mt-2">Click to change file</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-700 font-medium mb-1">Click to upload your track</p>
                        <p className="text-sm text-gray-500">MP3 or WAV up to 100MB</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="link" className="text-base font-semibold mb-2 block">
                    Streaming Link (Spotify, SoundCloud, YouTube) *
                  </Label>
                  <Input
                    id="link"
                    type="url"
                    placeholder="https://spotify.com/track/..."
                    value={streamingLink}
                    onChange={(e) => setStreamingLink(e.target.value)}
                    className="h-12"
                  />
                  <p className="text-sm text-gray-500 mt-2">Paste a link from Spotify, SoundCloud, or YouTube</p>
                </div>
              )}

              {/* Title */}
              <div>
                <Label htmlFor="title" className="text-base font-semibold mb-2 block">Track Title *</Label>
                <Input
                  id="title"
                  placeholder="Enter your track title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="h-12"
                />
              </div>

              {/* Artist */}
              <div>
                <Label htmlFor="artist" className="text-base font-semibold mb-2 block">Artist Name *</Label>
                <Input
                  id="artist"
                  placeholder="Your artist or band name"
                  value={formData.artist}
                  onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                  required
                  className="h-12"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-base font-semibold mb-2 block">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  We'll keep you updated on your submission and share tips to get the most out of your reviews. Unsubscribe anytime.
                </p>
              </div>

              {/* Genre */}
              <div>
                <Label htmlFor="genre" className="text-base font-semibold mb-2 block">Genre *</Label>
                <select
                  id="genre"
                  value={formData.genre}
                  onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                  className="w-full h-12 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black"
                  required
                >
                  <option value="Hip-Hop">Hip-Hop</option>
                  <option value="R&B">R&B</option>
                  <option value="Pop">Pop</option>
                  <option value="Rock">Rock</option>
                  <option value="Electronic">Electronic</option>
                  <option value="Country">Country</option>
                  <option value="Jazz">Jazz</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Submission Type */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Submission Type</Label>
                
                {/* Nudge: Priority queue hint */}
                <SubmitPageNudge className="mb-4" />
                
                {/* Skips Only Mode Warning */}
                {reviewerSession?.skips_only_mode && (
                  <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded-lg">
                    <p className="text-sm text-yellow-800 font-medium">
                      ⚠️ <strong>Insight Options Only:</strong> This reviewer is currently only accepting paid insight submissions. Choose one of the insight options below.
                    </p>
                  </div>
                )}
                
                {/* Paid Submissions Unavailable - Stripe not connected */}
                {selectedReviewerInfo && selectedReviewerInfo.can_accept_payments === false && (
                  <div className="mb-4 p-3 bg-gray-100 border border-gray-300 rounded-lg">
                    <p className="text-sm text-gray-700 font-medium">
                      ℹ️ <strong>Paid Submissions Unavailable:</strong> This reviewer is not set up to accept payments at this time. You can submit using the free Regular option.
                    </p>
                  </div>
                )}
                
                {/* Skip Line Payments Disabled Warning - only show if Stripe IS connected but skip is disabled */}
                {selectedReviewerInfo && selectedReviewerInfo.can_accept_payments !== false && selectedReviewerInfo.skip_line_enabled === false && (
                  <div className="mb-4 p-3 bg-gray-100 border border-gray-300 rounded-lg">
                    <p className="text-sm text-gray-700 font-medium">
                      ℹ️ <strong>Skip Line Not Available:</strong> This reviewer is not accepting skip payments at this time. You can submit using the free Regular option.
                    </p>
                  </div>
                )}
                
                <div className="space-y-3">
                  {/* Priority Options Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Skip - $5 */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (selectedReviewerInfo?.skip_line_enabled !== false && selectedReviewerInfo?.can_accept_payments !== false) {
                          trackEvent('tip_click', { tip_type: 'skip' });
                          setFormData({ ...formData, submissionType: 'skip' });
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                      className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                        formData.submissionType === 'skip' 
                          ? 'border-yellow-500 bg-yellow-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      } ${(selectedReviewerInfo?.skip_line_enabled === false || selectedReviewerInfo?.can_accept_payments === false) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-gray-900">Quick Insight</span>
                          <Popover>
                            <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="inline-flex">
                                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent side="top" className="w-64 bg-gray-900 text-white text-xs p-3 leading-relaxed border-0">
                              Around 15–30 seconds of live verbal feedback on your track. Includes encouragement and one clear improvement suggestion. Placed at the front of the queue.
                            </PopoverContent>
                          </Popover>
                        </div>
                        <span className="text-sm font-bold text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded">
                          {(selectedReviewerInfo?.skip_line_enabled === false || selectedReviewerInfo?.can_accept_payments === false) ? 'N/A' : '$5'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">Brief feedback</p>
                    </div>
                    
                    {/* Superskip - $10 */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (selectedReviewerInfo?.skip_line_enabled !== false && selectedReviewerInfo?.can_accept_payments !== false) {
                          trackEvent('tip_click', { tip_type: 'superskip' });
                          setFormData({ ...formData, submissionType: 'superskip' });
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                      className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                        formData.submissionType === 'superskip' 
                          ? 'border-red-500 bg-red-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      } ${(selectedReviewerInfo?.skip_line_enabled === false || selectedReviewerInfo?.can_accept_payments === false) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-gray-900">Detailed Insight</span>
                          <Popover>
                            <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="inline-flex">
                                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent side="top" className="w-64 bg-gray-900 text-white text-xs p-3 leading-relaxed border-0">
                              Around 30–60 seconds of live feedback with more detail and explanation. Placed at the top of the queue.
                            </PopoverContent>
                          </Popover>
                        </div>
                        <span className="text-sm font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded">
                          {(selectedReviewerInfo?.skip_line_enabled === false || selectedReviewerInfo?.can_accept_payments === false) ? 'N/A' : '$10'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">In-depth feedback</p>
                    </div>
                    
                    {/* Custom - $15+ */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (selectedReviewerInfo?.skip_line_enabled !== false && selectedReviewerInfo?.can_accept_payments !== false) {
                          trackEvent('tip_click', { tip_type: 'custom' });
                          setFormData({ ...formData, submissionType: 'custom' });
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                      className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                        formData.submissionType === 'custom' 
                          ? 'border-green-500 bg-green-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      } ${(selectedReviewerInfo?.skip_line_enabled === false || selectedReviewerInfo?.can_accept_payments === false) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-gray-900">Deep Dive</span>
                          <Popover>
                            <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="inline-flex">
                                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent side="top" className="w-64 bg-gray-900 text-white text-xs p-3 leading-relaxed border-0">
                              60 seconds or more of in-depth live feedback and discussion. Highest priority placement in the queue.
                            </PopoverContent>
                          </Popover>
                        </div>
                        <span className="text-sm font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded">
                          {(selectedReviewerInfo?.skip_line_enabled === false || selectedReviewerInfo?.can_accept_payments === false) ? 'N/A' : '$15+'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">Extended feedback session</p>
                    </div>
                  </div>
                  
                  {/* Free Option - Full Width */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => !reviewerSession?.skips_only_mode && setFormData({ ...formData, submissionType: 'regular' })}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      formData.submissionType === 'regular' 
                        ? 'border-gray-800 bg-gray-50 shadow-md' 
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    } ${reviewerSession?.skips_only_mode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-gray-900">Community Queue</span>
                        <Popover>
                          <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <button type="button" className="inline-flex">
                              <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent side="top" className="w-64 bg-gray-900 text-white text-xs p-3 leading-relaxed border-0">
                            Reviewed in order when time allows. No priority placement.
                          </PopoverContent>
                        </Popover>
                        <span className="text-xs text-gray-500 ml-1">— Reviewed in order when available</span>
                      </div>
                      <span className="text-sm font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                        {reviewerSession?.skips_only_mode ? 'N/A' : 'Free'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Custom tip amount input */}
                {formData.submissionType === 'custom' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <Label className="text-sm font-semibold mb-2 block">Custom Amount</Label>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold">$</span>
                      <Input
                        type="number"
                        min="15"
                        step="5"
                        value={customTipInput}
                        onChange={(e) => {
                          setCustomTipInput(e.target.value);
                        }}
                        onBlur={(e) => {
                          const dollars = parseInt(e.target.value) || 15;
                          const roundedDollars = Math.max(15, Math.round(dollars / 5) * 5);
                          setCustomTipInput(roundedDollars.toString());
                          setCustomTipAmount(roundedDollars * 100);
                        }}
                        className="w-24 h-10"
                      />
                      <span className="text-sm text-gray-500">(min $15)</span>
                    </div>
                  </div>
                )}
                
                {/* Clean helper text */}
                <p className="text-sm text-gray-500 mt-4 text-center">
                  Higher tiers get priority placement and more feedback time
                </p>
              </div>

              {/* Reviewer Selection */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Select Reviewer *</Label>
                
                {/* New Reviewers Carousel - Help artists discover new reviewers */}
                <NewReviewersCarousel 
                  onSelectReviewer={(reviewer) => {
                    if (reviewer.is_live) {
                      setFormData({ ...formData, reviewerId: reviewer.id });
                      trackEvent('new_reviewer_selected', { reviewer_id: reviewer.id });
                    } else {
                      toast({
                        title: "Reviewer Not Live",
                        description: `${reviewer.name} isn't live right now. Check back later or follow them to get notified!`,
                        variant: "default"
                      });
                    }
                  }}
                  selectedReviewerId={formData.reviewerId}
                />
                
                {loadingReviewers ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Loading reviewers...</p>
                  </div>
                ) : reviewers.filter(r => r.isLive).length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-semibold">No reviewers currently live and accepting submissions</p>
                    <p className="text-sm text-gray-500 mt-2">Please check back later or follow your favorite reviewers to get notified when they're available</p>
                  </div>
                ) : (
                  <div>
                    <select
                      id="reviewer"
                      value={formData.reviewerId}
                      onChange={(e) => setFormData({ ...formData, reviewerId: e.target.value })}
                      className="w-full h-12 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black"
                      required
                    >
                      <option value="">-- Choose a reviewer --</option>
                      {reviewers.filter(r => r.isLive).map((reviewer) => (
                        <option key={reviewer.id} value={reviewer.id}>
                          {reviewer.name} (@{reviewer.username}) 🔴 LIVE
                          {reviewer.queueCount > 0 && ` - ${reviewer.queueCount} in queue`}
                        </option>
                      ))}
                    </select>
                    {selectedReviewerInfo && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start gap-3">
                          {(() => {
                            const avatarUrl = getAvatarUrl(selectedReviewerInfo);
                            
                            return avatarUrl ? (
                              <img 
                                src={avatarUrl}
                                alt={selectedReviewerInfo.name}
                                className="w-12 h-12 rounded-full object-cover"
                                loading="eager"
                                onError={(e) => {
                                  // Replace with fallback on error
                                  e.target.style.display = 'none';
                                  const fallback = e.target.nextSibling;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            ) : null;
                          })()}
                          <div 
                            className="w-12 h-12 rounded-full bg-[#F2D857] flex items-center justify-center text-black font-bold"
                            style={{ 
                              display: getAvatarUrl(selectedReviewerInfo) ? 'none' : 'flex'
                            }}
                          >
                            {getInitials(selectedReviewerInfo.name)}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{selectedReviewerInfo.name}</p>
                            <p className="text-sm text-gray-600">@{selectedReviewerInfo.username}</p>
                            {selectedReviewerInfo.bio && (
                              <p className="text-sm text-gray-600 mt-1">{selectedReviewerInfo.bio}</p>
                            )}
                            {selectedReviewerInfo.isLive && (
                              <Badge className="mt-2 bg-red-500 text-white">
                                🔴 LIVE NOW
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Notification Consent Checkbox - Only shows when a specific reviewer is selected */}
              {selectedReviewerInfo && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.notifyOnLive}
                      onChange={(e) => setFormData({ ...formData, notifyOnLive: e.target.checked })}
                      className="mt-0.5 h-4 w-4 text-amber-500 border-gray-300 rounded focus:ring-amber-500"
                      data-testid="notify-on-live-checkbox"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        Notify me when {selectedReviewerInfo.name} goes live
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Get an email when they start a new session so you never miss a chance to submit!
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Notes */}
              <div>
                <Label htmlFor="notes" className="text-base font-semibold mb-2 block">
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  id="notes"
                  placeholder="Any specific feedback you&apos;re looking for?"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                />
              </div>
            </div>
          </Card>

          {/* Info Alert */}
          <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Before you submit:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Ensure your track is properly mixed and mastered</li>
                  <li>Check that all information is accurate</li>
                  <li>Insight options place you at the front of the priority review queue</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* P2: Payment Step Modal */}
          {showPaymentStep && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <div className="max-w-md w-full max-h-[90vh] overflow-y-auto">
                <PaymentStep
                  reviewerId={formData.reviewerId}
                  sessionId={reviewerSession?.session_id}
                  paymentType={formData.submissionType}
                  customAmount={formData.submissionType === 'custom' ? customTipAmount : undefined}
                  onPaymentComplete={handlePaymentComplete}
                  onCancel={handlePaymentCancel}
                  onDowngradeToFree={handleDowngradeToFree}
                  onBeforeStripeRedirect={saveFormStateForStripeRedirect}
                />
              </div>
            </div>
          )}

          {/* Submit Button - P1: Shows loading spinner and disabled during submission */}
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting || !isFormValid() || (reviewerSession && reviewerSession.submissions_enabled === false)}
            className="w-full bg-black hover:bg-[#F2D857] hover:text-black text-white text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (reviewerSession && reviewerSession.submissions_enabled === false) ? (
              '🚫 Submissions Closed'
            ) : isPaidSubmission() ? (
              `Continue to Payment ($${formData.submissionType === 'custom' ? (customTipAmount / 100).toFixed(0) : formData.submissionType === 'skip' ? '5' : '10'})`
            ) : (
              'Submit Track for Review'
            )}
          </Button>
          
          {!isFormValid() && !isSubmitting && !(reviewerSession && reviewerSession.submissions_enabled === false) && (
            <p className="text-center text-sm text-gray-500 mt-3">
              Please fill in all required fields and select {uploadType === 'file' ? 'a file' : 'a streaming link'}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default Upload;
